/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2018. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';

@Injectable({ providedIn: 'root' })
export class StorageService {
  private readonly LOGGED_OUT = 'logged_out';
  private readonly TOKEN_KEY = 'nextpro_token';

  constructor(private cookieService: CookieService, private router: Router) {
    this.listenForLogout();
  }

  getCookie(key): string {
    return this.cookieService.get(key);
  }

  setCookie(key, token, expiry = null) {
    let expireDate = expiry;
    if (expireDate === null) {
      expireDate = new Date();
      expireDate.setTime(expireDate.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days
    }
    this.cookieService.set(key, token, expireDate, '/');
  }

  setCookieExpiry(key, expiry) {
    if (!key || !expiry) {
      return false;
    }
    const cookieValue = this.cookieService.get(key);
    this.cookieService.set(key, cookieValue, expiry, '/');
  }

  deleteCookie(key) {
    this.cookieService.delete(key);
    if (key === this.TOKEN_KEY) {
      this.cookieService.delete(key, '/', '.nextpro.com');
    }
  }

  setLocalStorageItem(key: string, token: string) {
    if (this.localStorageAvailable()) {
      localStorage.setItem(key, token);
    }
  }

  getLocalStorageItem(key: string) {
    if (this.localStorageAvailable()) {
      return localStorage.getItem(key);
    } else {
      return null;
    }
  }

  deleteLocalStorageItem(key: string) {
    if (this.localStorageAvailable()) {
      localStorage.removeItem(key);
    }
  }

  getLocalStorageItemsByPrefix(prefix: string) {
    const items = [];
    for (const key in localStorage) {
      if (key.startsWith(prefix)) {
        items.push(key);
      }
    }
    return items;
  }

  // Set loggedOut token to send message to other opened tabs to log out
  setLogoutToken() {
    // if token exists, erase it to trigger storage event
    if (this.getLocalStorageItem(this.LOGGED_OUT)) {
      this.deleteLocalStorageItem(this.LOGGED_OUT);
    }
    this.setLocalStorageItem(this.LOGGED_OUT, this.LOGGED_OUT);

    // erase the logout token if still present after 1s
    setTimeout(() => {
      if (this.getLocalStorageItem(this.LOGGED_OUT)) {
        this.deleteLocalStorageItem(this.LOGGED_OUT);
      }
    }, 1000);
  }

  private localStorageAvailable() {
    return this.storageAvailable('localStorage');
  }

  private storageAvailable(type) {
    const storage: any = window[type];
    try {
      const x = '__storage_test__';
      storage.setItem(x, x);
      storage.removeItem(x);
      return true;
    } catch (e) {
      return (
        e instanceof DOMException &&
        // everything except Firefox
        (e.code === 22 ||
          // Firefox
          e.code === 1014 ||
          // test name field too, because code might not be present
          // everything except Firefox
          e.name === 'QuotaExceededError' ||
          // Firefox
          e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
        // acknowledge QuotaExceededError only if there's something already stored
        storage.length !== 0
      );
    }
  }

  private listenForLogout() {
    if (!window) {
      return;
    }
    window.removeEventListener('storage', this.logoutHandle.bind(this));
    window.addEventListener('storage', this.logoutHandle.bind(this));
  }

  private logoutHandle(event) {
    if (event.storageArea !== localStorage) {
      return;
    }
    if (event.key === this.LOGGED_OUT && event.newValue === this.LOGGED_OUT) {
      this.deleteLocalStorageItem(this.LOGGED_OUT);
      this.router.navigate(['/', 'logout']);
    }
  }
}
